import type { FilterSpecification, ModelLayerSpecification } from 'mapbox-gl';
import competitorStationModelUrl from '../assets/charging-station-competitor.glb?url';
import dealStationModelUrl from '../assets/charging-station-deal.glb?url';
import locationStationModelUrl from '../assets/charging-station-location.glb?url';
import chargingStationModelUrl from '../assets/charging-station.glb?url';
import { isMap3dEnabled } from './basemap';

/** The branded Electra charger. Locations and competitors recolor it. A deal keeps it and adds a work site. */
export const CHARGING_STATION_MODEL_URL = chargingStationModelUrl;

/**
 * One charger mesh. A location is grey concrete. A deal is that Electra
 * charger with construction around it. A competitor is a white charger with
 * the violet lamp of the competitor pins. Electra keeps the teal cabinet and mint lamp.
 */
export const STATION_MODEL_URLS = {
  electra: chargingStationModelUrl,
  location: locationStationModelUrl,
  deal: dealStationModelUrl,
  competitor: competitorStationModelUrl,
} as const;

export type StationModelVariant = keyof typeof STATION_MODEL_URLS;

/**
 * Mapbox only queues a model whose id contains "://" (or is registered on the
 * style). Vite's `?url` import is a root-relative path, so the mesh is never fetched.
 */
export function chargingStationModelId(variant: StationModelVariant = 'electra'): string {
  const url = STATION_MODEL_URLS[variant];
  if (url.includes('://')) return url;
  return new URL(url, window.location.href).href;
}

/**
 * The mesh is a dispenser about 2.2 m tall, with the status arms up to about
 * 3.4 m. Twice that still sits with the trees; the real height disappears
 * into the pavement at the zoom where the buildings appear.
 */
export const STATION_MODEL_SCALE = 2;

/** With the 3D buildings. Below this the station is only a few pixels. */
export const STATION_MODEL_MIN_ZOOM = 15;

export function chargingStationModelLayer(
  id: string,
  source: string,
  map: object,
  options?: {
    sourceLayer?: string;
    filter?: FilterSpecification;
    variant?: StationModelVariant;
  },
): ModelLayerSpecification {
  return {
    id,
    type: 'model',
    source,
    minzoom: STATION_MODEL_MIN_ZOOM,
    ...(options?.sourceLayer ? { 'source-layer': options.sourceLayer } : {}),
    ...(options?.filter ? { filter: options.filter } : {}),
    layout: {
      'model-id': chargingStationModelId(options?.variant ?? 'electra'),
      visibility: isMap3dEnabled(map) ? 'visible' : 'none',
    },
    paint: {
      // Over buildings, on the ground. The symbol mark stays above the labels.
      'model-type': 'location-indicator',
      'model-scale': [STATION_MODEL_SCALE, STATION_MODEL_SCALE, STATION_MODEL_SCALE],
      'model-elevation-reference': 'ground',
      'model-cast-shadows': true,
      'model-receive-shadows': false,
    },
  };
}
